import React, { useMemo, useState } from "react";
import Accordion from "@components/Accordion/Accordion";
import { SingleDetail } from "@features/vessel-nomination/add/components/header/VesselDetails";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { type Company } from "@services/vessel-call/types";
import type { VesselNominationHeaderResponse } from "@services/vessel-nomination/types";
import { capitalizeWord } from "@utils/string";
import { useTranslation } from "react-i18next";

const DETAILS_LANG_BASE = "vessel-nomination.add.details";
const LANG_BASE = "vessel-nomination.dry-bulk.add";

interface DryBulkVesselDetailsProps {
  testId: string;
  header: VesselNominationHeaderResponse | undefined;
  agencyCompanyId?: string;
}

const DryBulkVesselDetails: React.FC<DryBulkVesselDetailsProps> = ({
  testId,
  header,
  agencyCompanyId,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(true);

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const agencyName = useMemo(
    () =>
      companies.find((c) => c.companyCode === (agencyCompanyId ?? user?.companyId))
        ?.companyNameEnglish,
    [companies, user, agencyCompanyId],
  );

  const flag =
    header?.countryCode && header?.countryName
      ? `${header.countryCode}, ${capitalizeWord(header.countryName)}`
      : undefined;

  const location = [
    { label: t(`${DETAILS_LANG_BASE}.vessel-location`), value: header?.currentLocation },
    { label: t(`${LANG_BASE}.vessel-call`), value: header?.activeVesselCall?.vcn },
  ];
  const vesselGrid = [
    { label: t(`${DETAILS_LANG_BASE}.imo-number`), value: header?.vesselImoNumber },
    { label: t(`${DETAILS_LANG_BASE}.flag`), value: flag },
    { label: t(`${DETAILS_LANG_BASE}.loa`), value: header?.loa },
    { label: t(`${DETAILS_LANG_BASE}.vessel-condition`), value: header?.condition },
    { label: t(`${DETAILS_LANG_BASE}.vessel-name`), value: header?.vesselName },
    {
      label: t(`${DETAILS_LANG_BASE}.agency`),
      value: agencyName ? capitalizeWord(agencyName) : undefined,
    },
    { label: t(`${DETAILS_LANG_BASE}.beam`), value: header?.beam },
    { label: t(`${DETAILS_LANG_BASE}.gross-tonnage`), value: header?.grt },
    { label: t(`${DETAILS_LANG_BASE}.type`), value: header?.vesselTypeDesc?.toUpperCase() },
    { label: t(`${DETAILS_LANG_BASE}.age`), value: header?.vesselAgeYears },
    { label: t(`${DETAILS_LANG_BASE}.max-draft`), value: header?.draftMax },
  ];

  return (
    <Accordion
      testId={`${testId}-vessel-details`}
      summary={t(`${DETAILS_LANG_BASE}.title`)}
      expanded={isOpen}
      onChange={() => setIsOpen(!isOpen)}
      isTransparent={true}
    >
      <div className="grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,4fr)]">
        <div
          className="space-y-6 md:border-e md:border-[#E5E7EB] md:pe-8"
          data-testid={`${testId}-vessel-details-location`}
        >
          {location.map((item) => (
            <SingleDetail key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
        <div
          className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-4"
          data-testid={`${testId}-vessel-details-grid`}
        >
          {vesselGrid.map((item) => (
            <SingleDetail key={item.label} label={item.label} value={item.value} />
          ))}
        </div>
      </div>
    </Accordion>
  );
};

export default DryBulkVesselDetails;
